import React, { useState, useEffect } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { EDIT_MODE } from '../../../../../../common/constants';
import type { GoalWithIds } from '../../../../../../common/types';
import { MAX_REACHED } from '../../../../../../common/ui/constants.tsx';
import { GoalHeader } from '../../../../../../common/ui/goals/goal-header';
import { AddGoalButton } from './add-goal-button';
import { LimitInline } from './limit/main.tsx';
import { getLimitState } from './limit/utils.tsx';
import { GoalListView } from './main';
import { useGoalsState } from './state';
import type { Props } from './types';

export const Goals = ({
	defaultGoals,
	draggableGoals,
	maxSlaGoalsLimit,
	getFormValues,
	metricId,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [defaultGoalList, setDefaultGoalsList] = useState<GoalWithIds>(defaultGoals);
	const [draggableGoalList, setDraggableGoalsList, onDragEnd, onAppendNewGoal] =
		useGoalsState(draggableGoals);
	const currentGoalsCount = defaultGoalList.length + draggableGoalList.length;

	const remainingGoals = maxSlaGoalsLimit - currentGoalsCount;

	const goalLimitState = getLimitState(maxSlaGoalsLimit, remainingGoals);

	useEffect(
		() =>
			// ComponentWillUnmount
			() => {
				if (goalLimitState === MAX_REACHED) {
					// Fire an event when the max goal limit is reached
					fireTrackAnalytics(createAnalyticsEvent({}), 'jsmSlaConfiguration slaGoalsLimitReached', {
						slaGoalsLimit: maxSlaGoalsLimit,
					});
				}
			},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[createAnalyticsEvent, goalLimitState],
	);

	return (
		<table>
			<LimitInline goalsLimit={maxSlaGoalsLimit} remaining={remainingGoals} />
			<GoalHeader mode={EDIT_MODE} />
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="editable-goals-list">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps}>
							<GoalListView
								goals={draggableGoalList}
								setGoals={setDraggableGoalsList}
								getFormValues={getFormValues}
								isDraggable
							/>
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
			<GoalListView
				goals={defaultGoalList}
				setGoals={setDefaultGoalsList}
				getFormValues={getFormValues}
			/>
			{goalLimitState !== MAX_REACHED && (
				<AddGoalButton onAddRequested={() => onAppendNewGoal(metricId)} />
			)}
			<LimitInline goalsLimit={maxSlaGoalsLimit} remaining={remainingGoals} />
		</table>
	);
};
